/**
 * Ayurveda dosha-aligned sleep window calculator.
 * Pure functions. Sunrise/sunset via inline solar math (no Suncalc dep).
 *
 * Sources: classical Ashtanga Hridayam ratri-charya — Pitta kaal 22:00-02:00,
 * deep sleep onset must precede 22:00 for restorative rest. Adjusted to user's
 * solar window so the math survives travel/geography shifts.
 */

export type Dosha = 'vata' | 'pitta' | 'kapha' | 'vata-pitta' | 'pitta-kapha' | 'vata-kapha' | 'tridoshic';

export interface DoshaProfile {
  primary: Dosha;
  bedtime_hour: number;
  wake_hour: number;
  ideal_duration_h: number;
  rationale_hi: string;
  rationale_en: string;
}

const DOSHA_TARGETS: Record<Dosha, Omit<DoshaProfile, 'primary'>> = {
  vata: {
    bedtime_hour: 22,
    wake_hour: 6.5,
    ideal_duration_h: 8.5,
    rationale_hi: 'vata ko zyada aur jaldi neend chahiye, regularity sabse zyada',
    rationale_en: 'vata needs the most sleep and earliest bedtime; consistency is non-negotiable',
  },
  pitta: {
    bedtime_hour: 22.5,
    wake_hour: 6,
    ideal_duration_h: 7.5,
    rationale_hi: 'pitta ka pitta-kaal 10pm-2am pe deep sleep zaroori',
    rationale_en: 'pitta needs deep sleep across the 10pm-2am pitta phase',
  },
  kapha: {
    bedtime_hour: 23,
    wake_hour: 5.5,
    ideal_duration_h: 6.5,
    rationale_hi: 'kapha jaldi uthe, varna sustii badhti hai',
    rationale_en: 'kapha must rise before 6 to avoid heaviness; less sleep, but quality',
  },
  'vata-pitta': {
    bedtime_hour: 22.25,
    wake_hour: 6.25,
    ideal_duration_h: 8,
    rationale_hi: 'vata-pitta dono ko shaant rakhne ke liye 10pm bedtime',
    rationale_en: 'vata-pitta combination: 10pm bedtime calms both',
  },
  'pitta-kapha': {
    bedtime_hour: 22.75,
    wake_hour: 5.75,
    ideal_duration_h: 7,
    rationale_hi: 'pitta-kapha: jaldi uthna, deep sleep window pakadna',
    rationale_en: 'pitta-kapha: rise early, anchor the deep sleep window',
  },
  'vata-kapha': {
    bedtime_hour: 22.5,
    wake_hour: 6,
    ideal_duration_h: 7.5,
    rationale_hi: 'vata-kapha: balance, na bahut zyada na bahut kam',
    rationale_en: 'vata-kapha: balance, neither too much nor too little',
  },
  tridoshic: {
    bedtime_hour: 22.5,
    wake_hour: 6.25,
    ideal_duration_h: 7.75,
    rationale_hi: 'tridoshic: madhyam maarg, niyamit',
    rationale_en: 'tridoshic: middle path, regular',
  },
};

export function profileForDosha(d: Dosha): DoshaProfile {
  return { primary: d, ...DOSHA_TARGETS[d] };
}

export interface DoshaQuestionnaire {
  body_frame: 'thin' | 'medium' | 'sturdy';
  hunger: 'irregular' | 'sharp' | 'slow';
  sleep_natural: 'light' | 'sound' | 'heavy';
  energy: 'bursts' | 'steady' | 'consistent_slow';
  temperament: 'creative_anxious' | 'driven_irritable' | 'calm_attached';
}

export function classifyDosha(q: DoshaQuestionnaire): Dosha {
  const scores = { vata: 0, pitta: 0, kapha: 0 };
  if (q.body_frame === 'thin') scores.vata++;
  if (q.body_frame === 'medium') scores.pitta++;
  if (q.body_frame === 'sturdy') scores.kapha++;
  if (q.hunger === 'irregular') scores.vata++;
  if (q.hunger === 'sharp') scores.pitta++;
  if (q.hunger === 'slow') scores.kapha++;
  if (q.sleep_natural === 'light') scores.vata++;
  if (q.sleep_natural === 'sound') scores.pitta++;
  if (q.sleep_natural === 'heavy') scores.kapha++;
  if (q.energy === 'bursts') scores.vata++;
  if (q.energy === 'steady') scores.pitta++;
  if (q.energy === 'consistent_slow') scores.kapha++;
  if (q.temperament === 'creative_anxious') scores.vata++;
  if (q.temperament === 'driven_irritable') scores.pitta++;
  if (q.temperament === 'calm_attached') scores.kapha++;
  const ranked = (['vata', 'pitta', 'kapha'] as const).map((k) => ({ k, v: scores[k] })).sort((a, b) => b.v - a.v);
  const top = ranked[0];
  const second = ranked[1];
  if (!top || !second) return 'tridoshic';
  if (top.v === second.v && top.v === (ranked[2]?.v ?? -1)) return 'tridoshic';
  if (top.v - second.v <= 1) {
    const pair: string = [top.k, second.k].sort().join('-');
    if (pair === 'pitta-vata') return 'vata-pitta';
    if (pair === 'kapha-pitta') return 'pitta-kapha';
    if (pair === 'kapha-vata') return 'vata-kapha';
    return pair as Dosha;
  }
  return top.k;
}

export interface SolarWindow {
  sunrise_h: number;
  sunset_h: number;
  brahma_muhurta_h: number;
}

export function solarFor(date: Date, lat: number, lon: number): SolarWindow {
  const rad = Math.PI / 180;
  const dayOfYear = Math.floor((date.getTime() - Date.UTC(date.getUTCFullYear(), 0, 0)) / 86400000);
  const declination = 23.45 * Math.sin(((360 / 365) * (dayOfYear - 81)) * rad);
  const hourAngleCos = -Math.tan(lat * rad) * Math.tan(declination * rad);
  const clamped = Math.max(-1, Math.min(1, hourAngleCos));
  const hourAngle = Math.acos(clamped) / rad;
  const solarNoon = 12 - lon / 15;
  const sunrise = solarNoon - hourAngle / 15;
  const sunset = solarNoon + hourAngle / 15;
  return {
    sunrise_h: clamp(sunrise, 4, 8),
    sunset_h: clamp(sunset, 16, 20),
    brahma_muhurta_h: clamp(sunrise - 1.5, 3, 6),
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export function alignedSleepWindow(p: DoshaProfile, solar: SolarWindow): { bed_h: number; wake_h: number; brahma_muhurta_h: number } {
  const wake = Math.max(solar.brahma_muhurta_h + 0.5, p.wake_hour);
  const bed = wake - p.ideal_duration_h + (wake < 0 ? 24 : 0);
  return { bed_h: bed < 0 ? bed + 24 : bed, wake_h: wake, brahma_muhurta_h: solar.brahma_muhurta_h };
}

export function formatClock(h: number): string {
  const hr = Math.floor(h);
  const min = Math.round((h - hr) * 60);
  const hh = ((hr % 24) + 24) % 24;
  const mm = min.toString().padStart(2, '0');
  return `${hh.toString().padStart(2, '0')}:${mm}`;
}
